/**
 * SocialPostHistory — publicaciones creadas desde SML, con estado por red.
 *
 * Los estados se actualizan cuando el usuario abre esta vista o toca
 * «Actualizar»: no hay poller permanente (la cuota del proveedor es
 * compartida por toda la aplicación).
 */
import React, { useCallback, useEffect, useState } from 'react';
import {
  AlertTriangle, CalendarClock, CheckCircle2, Clock, Loader2,
  RefreshCw, Trash2, XCircle,
} from 'lucide-react';
import { toast } from 'sonner';
import { socialService } from '../../services/socialService';
import {
  PlatformIcon, POST_STATUS_LABEL, TARGET_STATUS_LABEL,
} from './platforms';

const STATUS_TONE = {
  published: 'ok', scheduled: 'info', partial: 'warn', failed: 'fail',
  cancelled: 'muted', publishing: 'info', draft: 'muted',
};

const formatDate = (value) => {
  if (!value) return '—';
  try {
    return new Date(value).toLocaleString('es-AR', {
      day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit',
    });
  } catch { return value; }
};

export const SocialPostHistory = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await socialService.listPosts();
      setPosts(data.posts || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'No pudimos cargar el historial.');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const refresh = async (postId) => {
    setBusy(postId);
    try {
      await socialService.refreshPost(postId);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'No pudimos actualizar el estado.');
    } finally { setBusy(null); }
  };

  const cancel = async (postId) => {
    if (!window.confirm('¿Cancelar esta publicación pendiente?')) return;
    setBusy(postId);
    try {
      const data = await socialService.cancelPost(postId);
      if (data.not_cancelled > 0) toast.warning('Algunas redes no permitieron cancelar.');
      else toast.success('Publicación cancelada');
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'No pudimos cancelar la publicación.');
    } finally { setBusy(null); }
  };

  if (loading) {
    return <div className="social-loading" data-testid="social-history-loading">
      <Loader2 size={16} className="animate-spin" /> Cargando historial…
    </div>;
  }

  if (posts.length === 0) {
    return <div className="social-empty small" data-testid="social-history-empty">
      <p>Todavía no publicaste nada desde Studio Media Labs.</p>
    </div>;
  }

  return (
    <div className="social-history" data-testid="social-history">
      <div className="social-history-head">
        <h4>Publicaciones</h4>
        <button className="social-btn ghost" onClick={load} data-testid="social-history-reload">
          <RefreshCw size={13} /> Recargar
        </button>
      </div>
      <ul>
        {posts.map((post) => {
          const pending = (post.targets || []).some(
            (t) => ['scheduled', 'pending', 'draft'].includes(t.status));
          return (
            <li key={post.id} data-testid={`social-post-${post.id}`}>
              <div className="social-history-row">
                <span className={`social-chip ${STATUS_TONE[post.status] || 'muted'}`}
                      data-testid={`social-post-status-${post.id}`}>
                  {post.status === 'published' ? <CheckCircle2 size={11} />
                    : post.status === 'failed' ? <XCircle size={11} />
                    : post.status === 'partial' ? <AlertTriangle size={11} />
                    : <Clock size={11} />}
                  {POST_STATUS_LABEL[post.status] || post.status}
                </span>
                <div className="social-history-main">
                  <p className="social-history-caption">{post.caption || <em>sin texto</em>}</p>
                  <span className="social-history-meta">
                    {post.publish_mode === 'scheduled'
                      ? <><CalendarClock size={11} /> {formatDate(post.scheduled_at)}
                        {post.timezone ? ` · ${post.timezone}` : ''}</>
                      : <><Clock size={11} /> {formatDate(post.created_at)}</>}
                  </span>
                </div>
                <div className="social-history-actions">
                  <button className="social-btn ghost" onClick={() => refresh(post.id)}
                          disabled={busy === post.id}
                          data-testid={`social-post-refresh-${post.id}`}>
                    {busy === post.id
                      ? <Loader2 size={12} className="animate-spin" />
                      : <RefreshCw size={12} />}
                    Actualizar
                  </button>
                  {pending && (
                    <button className="social-btn danger" onClick={() => cancel(post.id)}
                            disabled={busy === post.id}
                            data-testid={`social-post-cancel-${post.id}`}>
                      <Trash2 size={12} /> Cancelar
                    </button>
                  )}
                </div>
              </div>
              <ul className="social-target-list">
                {(post.targets || []).map((target) => (
                  <li key={target.id} className={target.status}
                      data-testid={`social-target-${target.id}`}>
                    <PlatformIcon platform={target.platform} size={12} />
                    <span className="social-target-name">
                      {target.channel?.display_name || target.platform_label}
                    </span>
                    <span className={`social-chip ${target.status === 'published' ? 'ok'
                      : target.status === 'failed' ? 'fail' : 'info'}`}>
                      {TARGET_STATUS_LABEL[target.status] || target.status}
                    </span>
                    {target.error_message && (
                      <span className="social-target-error">{target.error_message}</span>
                    )}
                    {target.external_link && (
                      <a href={target.external_link} target="_blank" rel="noopener noreferrer">Ver</a>
                    )}
                    {target.provider_post_id && (
                      <code title="Identificador del proveedor (diagnóstico interno)">
                        {target.provider_post_id}
                      </code>
                    )}
                  </li>
                ))}
              </ul>
            </li>
          );
        })}
      </ul>
    </div>
  );
};

export default SocialPostHistory;
